import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { Alert, Box, CircularProgress, Dialog, DialogContent, DialogTitle } from "@mui/material"
import { ContextoGeral } from "../../../contextos/ContextoGeral"
import ServicoAPI from "../../../servicos/ServicoAPI"
import {
	atualizarLocalizacaoProfissionalGestaoVaga,
	obterLocalizacaoProfissionalGestaoVaga,
} from "../../../servicos/gestaoVagaLocalizacao"
import { atualizarGeoERecarregarGestaoVagaHub } from "./gestaoVagaAtualizarGeo.js"
import GestaoVagaDetalheSheet from "./GestaoVagaDetalheSheet.jsx"
import GestaoVagaFilaCard from "./GestaoVagaFilaCard.jsx"
import { mergeEstadoFilaNoDetalhe } from "./gestaoVagaFilaUi.js"
import {
	ABA_FILA_AGORA,
	ABA_FILA_OUTROS,
	classificarVagaFilaTab,
	contarVagasPorAbaFila,
	filtrarVagasPorAbaFila,
	ordenarVagasFilaAgora,
	resolverAbaInicialFila,
} from "./gestaoVagaFilaLayout.js"
import { normalizarVagaFilaCard } from "./gestaoVagaHubApi.js"
import {
	aplicarRotuloVagaTexto,
	resolverRotulosGestaoVaga,
} from "./gestaoVagaRotuloUtil.js"

/**
 * Conteúdo da aba «Fila de hoje» dentro do hub unificado (GestaoVaga.jsx).
 */
export default function GestaoVagaAbaFila({
	vagasBrutas = [],
	diagnostico = null,
	onRecarregarHub,
	onAtualizarLocalizacaoHub,
	atualizandoGeoHub = false,
	idVagaDestaque = 0,
	rotulos,
}) {
	const { dados } = useContext(ContextoGeral)
	const txt = (texto) =>
		aplicarRotuloVagaTexto(texto, rotulos ?? resolverRotulosGestaoVaga())
	const vagas = useMemo(
		() =>
			(Array.isArray(vagasBrutas) ? vagasBrutas : [])
				.map(normalizarVagaFilaCard)
				.filter(Boolean),
		[vagasBrutas]
	)

	/** Feedback da API por vaga — exibido no card, acima dos botões */
	const [feedbackAcao, setFeedbackAcao] = useState(null)
	const [erroDialogSaida, setErroDialogSaida] = useState("")
	const [processandoId, setProcessandoId] = useState(0)
	const [atualizandoGeoLocal, setAtualizandoGeoLocal] = useState(false)
	const atualizandoGeo = atualizandoGeoHub || atualizandoGeoLocal
	const [abaFila, setAbaFila] = useState(ABA_FILA_AGORA)
	const [abaInicializada, setAbaInicializada] = useState(false)
	const [confirmarSaida, setConfirmarSaida] = useState(null)
	const [detalheAberto, setDetalheAberto] = useState(false)
	const [detalheCarregando, setDetalheCarregando] = useState(false)
	const [detalheVaga, setDetalheVaga] = useState(null)

	const { agora: qtdAgora, outros: qtdOutros } = useMemo(
		() => contarVagasPorAbaFila(vagas),
		[vagas]
	)

	const vagasExibidas = useMemo(() => {
		const filtradas = filtrarVagasPorAbaFila(vagas, abaFila)
		return abaFila === ABA_FILA_AGORA ? ordenarVagasFilaAgora(filtradas) : filtradas
	}, [vagas, abaFila])

	useEffect(() => {
		if (abaInicializada) return
		setAbaFila(resolverAbaInicialFila({ vagas, idVagaDestaque, abaQuery: "" }))
		setAbaInicializada(true)
	}, [vagas, idVagaDestaque, abaInicializada])

	const recarregarSilencioso = useCallback(
		async (geo) => {
			if (typeof onRecarregarHub === "function") {
				await onRecarregarHub({ silencioso: true, geo: geo ?? null })
			}
		},
		[onRecarregarHub]
	)

	const recarregarDetalheFilaComGeo = useCallback(
		async (idVaga, geo) => {
			if (!idVaga || !geo) return
			try {
				const r = await ServicoAPI.gestaoVagasDetalheApp(dados, idVaga, geo)
				if (r?.resultado === true && r?.dados) {
					const daLista = vagas.find((v) => v.id === idVaga)
					setDetalheVaga(mergeEstadoFilaNoDetalhe(r.dados, daLista))
				}
			} catch {
				/* mantém detalhe anterior */
			}
		},
		[dados, vagas]
	)

	const atualizarLocalizacaoNaTela = useCallback(async () => {
		let resultado = { ok: false, geo: null }
		if (typeof onAtualizarLocalizacaoHub === "function") {
			resultado = (await onAtualizarLocalizacaoHub()) || { ok: false, geo: null }
		} else {
			setAtualizandoGeoLocal(true)
			try {
				resultado = await atualizarGeoERecarregarGestaoVagaHub(onRecarregarHub)
			} finally {
				setAtualizandoGeoLocal(false)
			}
		}
		if (resultado.ok && detalheAberto && detalheVaga?.id && resultado.geo) {
			await recarregarDetalheFilaComGeo(detalheVaga.id, resultado.geo)
		}
		return resultado.ok
	}, [
		onAtualizarLocalizacaoHub,
		onRecarregarHub,
		detalheAberto,
		detalheVaga?.id,
		recarregarDetalheFilaComGeo,
	])

	const definirFeedbackVaga = (idVaga, tipo, mensagem) => {
		setFeedbackAcao({ idVaga, tipo, mensagem: String(mensagem || "").trim() })
	}

	const limparFeedbackVaga = (idVaga) => {
		setFeedbackAcao((prev) => (prev?.idVaga === idVaga ? null : prev))
	}

	const entrarFila = async (idVaga) => {
		setProcessandoId(idVaga)
		limparFeedbackVaga(idVaga)
		try {
			const geo = await atualizarLocalizacaoProfissionalGestaoVaga()
			if (!geo) {
				definirFeedbackVaga(idVaga, "error", "Ative a localização para entrar na fila.")
				return
			}
			const r = await ServicoAPI.gestaoVagasEntrarFilaApp(dados, idVaga, geo)
			if (r?.resultado === true) {
				definirFeedbackVaga(idVaga, "success", r?.mensagem || "Você entrou na fila!")
				await recarregarSilencioso(geo)
			} else {
				definirFeedbackVaga(
					idVaga,
					"error",
					r?.mensagem || "Não foi possível entrar na fila."
				)
			}
		} catch {
			definirFeedbackVaga(idVaga, "error", "Erro de conexão ao entrar na fila.")
		} finally {
			setProcessandoId(0)
		}
	}

	const solicitarSairFila = (idVaga) => {
		const v = vagas.find((item) => item.id === idVaga)
		setErroDialogSaida("")
		setConfirmarSaida({ id: idVaga, titulo: v?.titulo || txt("esta vaga") })
	}

	const sairFila = async (idVaga) => {
		setProcessandoId(idVaga)
		setErroDialogSaida("")
		limparFeedbackVaga(idVaga)
		try {
			const r = await ServicoAPI.gestaoVagasSairFilaApp(dados, idVaga)
			if (r?.resultado === true) {
				setConfirmarSaida(null)
				definirFeedbackVaga(idVaga, "success", r?.mensagem || "Você saiu da fila.")
				await recarregarSilencioso()
			} else {
				setErroDialogSaida(r?.mensagem || "Não foi possível sair da fila.")
			}
		} catch {
			setErroDialogSaida("Erro de conexão ao sair da fila.")
		} finally {
			setProcessandoId(0)
		}
	}

	const abrirDetalhesVaga = async (idVaga) => {
		const daLista = vagas.find((v) => v.id === idVaga)
		setDetalheAberto(true)
		setDetalheCarregando(true)
		setDetalheVaga(null)
		try {
			const geo = await obterLocalizacaoProfissionalGestaoVaga()
			if (!geo) {
				setDetalheVaga({ erro: txt("Ative a localização para ver os detalhes da vaga.") })
				return
			}
			const r = await ServicoAPI.gestaoVagasDetalheApp(dados, idVaga, geo)
			if (r?.resultado === true && r?.dados) {
				setDetalheVaga(mergeEstadoFilaNoDetalhe(r.dados, daLista))
			} else {
				setDetalheVaga({ erro: txt(r?.mensagem || "Não foi possível carregar os detalhes da vaga.") })
			}
		} catch {
			setDetalheVaga({ erro: "Erro de conexão ao carregar detalhes." })
		} finally {
			setDetalheCarregando(false)
		}
	}

	const listaVaziaGlobal = vagas.length === 0
	const listaVaziaAba = vagas.length > 0 && vagasExibidas.length === 0

	return (
		<>
			<div className="gv-fila-banner">
				<p className="gv-fila-banner__texto m-0">
					<strong>Fila de hoje:</strong> {txt("perto da vaga e no horário, toque em")}{" "}
					<strong>{txt("Entrar na fila de hoje")}</strong>.
				</p>
			</div>

			{listaVaziaGlobal ? (
				<Alert severity="info" className="gv-alert-linha-toda">
					{txt(
						diagnostico?.aviso ||
							diagnostico?.erroSqlTitulares ||
							"Você não é titular de nenhuma vaga ativa no momento."
					)}
				</Alert>
			) : (
				<>
					<div className="gv-fila-tabs" role="tablist" aria-label="Fila do dia">
						<button
							type="button"
							role="tab"
							aria-selected={abaFila === ABA_FILA_AGORA}
							className={`gv-lista-tab${abaFila === ABA_FILA_AGORA ? " gv-lista-tab--ativa" : ""}`}
							onClick={() => setAbaFila(ABA_FILA_AGORA)}
						>
							Agora
							<span className="gv-lista-tab__badge">{qtdAgora}</span>
						</button>
						<button
							type="button"
							role="tab"
							aria-selected={abaFila === ABA_FILA_OUTROS}
							className={`gv-lista-tab${abaFila === ABA_FILA_OUTROS ? " gv-lista-tab--ativa" : ""}`}
							onClick={() => setAbaFila(ABA_FILA_OUTROS)}
						>
							Outros dias
							<span className="gv-lista-tab__badge">{qtdOutros}</span>
						</button>
					</div>

					{listaVaziaAba ? (
						<Alert severity="info" className="mb-3 gv-alert-linha-toda">
							Nenhuma vaga nesta aba.
						</Alert>
					) : (
						<Box className="gv-fila-lista flex flex-col gap-3">
							{vagasExibidas.map((v) => (
								<GestaoVagaFilaCard
									key={v.id}
									vaga={v}
									variant={classificarVagaFilaTab(v) === ABA_FILA_AGORA ? "agora" : "outros"}
									destaque={idVagaDestaque > 0 && Number(v.id) === idVagaDestaque}
									atualizandoGeo={atualizandoGeo}
									carregando={processandoId > 0}
									processandoId={processandoId}
									feedbackAcao={
										feedbackAcao?.idVaga === v.id
											? { tipo: feedbackAcao.tipo, mensagem: feedbackAcao.mensagem }
											: null
									}
									onAtualizarLocalizacao={async () => {
										limparFeedbackVaga(v.id)
										const ok = await atualizarLocalizacaoNaTela()
										if (!ok) {
											definirFeedbackVaga(
												v.id,
												"error",
												"Não foi possível obter sua localização."
											)
										}
									}}
									onErroGps={(m) =>
										definirFeedbackVaga(v.id, "error", m || "Não foi possível abrir no mapa.")
									}
									onEntrarFila={entrarFila}
									onSairFila={solicitarSairFila}
									onDetalhes={abrirDetalhesVaga}
								/>
							))}
						</Box>
					)}
				</>
			)}

			<Dialog
				open={Boolean(confirmarSaida)}
				onClose={() => {
					if (!processandoId) {
						setConfirmarSaida(null)
						setErroDialogSaida("")
					}
				}}
			>
				<DialogTitle>Sair da fila?</DialogTitle>
				<DialogContent>
					<p className="m-0 mb-3">
						Deseja sair da fila de <strong>{confirmarSaida?.titulo}</strong>?
					</p>
					{erroDialogSaida ? (
						<Alert severity="error" className="mb-3 gv-alert-linha-toda">
							{erroDialogSaida}
						</Alert>
					) : null}
					<div className="flex gap-2">
						<button type="button" className="btnFullW" onClick={() => setConfirmarSaida(null)}>
							Cancelar
						</button>
						<button
							type="button"
							className="btnFullW bg-red-600 text-white"
							onClick={() => confirmarSaida?.id && sairFila(confirmarSaida.id)}
						>
							Sair da fila
						</button>
					</div>
				</DialogContent>
			</Dialog>

			<GestaoVagaDetalheSheet
				aberto={detalheAberto}
				carregando={detalheCarregando}
				vaga={detalheVaga}
				participacaoFila
				rotulos={rotulos}
				onAtualizarLocalizacaoFila={atualizarLocalizacaoNaTela}
				atualizandoGeoFila={atualizandoGeo}
				onFechar={() => {
					setDetalheAberto(false)
					setDetalheVaga(null)
				}}
			/>
		</>
	)
}
